import React from 'react';
import { Popover, Progress, Divider, Typography } from 'antd';
import {
  Coins,
  UserPlus,
  ArrowRightCircle,
  Unlock,
  FileText,
  Sparkles,
  ChevronDown,
  ArrowUpRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCreditStatus } from '../../hooks/useCreditStatus';
import { lerSaldo, textoDeSaldo } from './lib/saldoDeCreditos';
import { useOperationCosts } from '../../hooks/useOperationCosts';
import { textoDeCusto } from './lib/custoDeOperacao';

const { Text } = Typography;

interface OperationRate {
  icon: React.ReactNode;
  labelKey: string;
  fallback: string;
  /** Slug do catálogo — o preço vem de lá, nunca daqui. */
  slug: string;
}

/**
 * O que a tabela LISTA. O que cada linha CUSTA vem do catálogo
 * (`useOperationCosts`) — ver `services/operationCostsApi` para os cinco
 * slugs e a medição que mostrou "Gerar Briefing: 5" contra `credit_cost: 2`.
 */
const OPERATION_RATES: OperationRate[] = [
  { icon: <UserPlus size={13} />, labelKey: 'credits.opCreateProspect', fallback: 'Criar Prospect', slug: 'PROSPECT_CREATE' },
  { icon: <ArrowRightCircle size={13} />, labelKey: 'credits.opPromoteDeal', fallback: 'Promover para Deal', slug: 'PROSPECT_PROMOTE_DEAL' },
  { icon: <Unlock size={13} />, labelKey: 'credits.opRevealContact', fallback: 'Revelar Contato', slug: 'CONTACT_REVEAL' },
  { icon: <FileText size={13} />, labelKey: 'credits.opGenerateBriefing', fallback: 'Gerar Briefing', slug: 'SDR_MEETING_PREP' },
  { icon: <Sparkles size={13} />, labelKey: 'credits.opPersonalizeEmail', fallback: 'Personalizar Email', slug: 'SDR_TEMPLATE_PERSONALIZE' },
];

const CreditBalancePopover: React.FC = () => {
  const { creditStatus } = useCreditStatus();
  const { custoDe } = useOperationCosts();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // `?? 0` aqui renderizava "0 de 0 · 0% restante" com a barra vazia enquanto
  // `GET /credits/status` estava em voo (ou depois de falhar) — lido por
  // qualquer pessoa como "sua conta acabou". Ver `lib/saldoDeCreditos`.
  const saldo = lerSaldo(creditStatus);

  // Sem leitura não há nível de alerta a pintar: o cinza diz "não sei", e o
  // índigo (o "ok" do tema) afirmaria saúde que ninguém mediu.
  const alertColor = !saldo.conhecido
    ? '#CBD5E1'
    : creditStatus?.alertLevel === 'critical'
      ? '#EF4444'
      : creditStatus?.alertLevel === 'warning'
        ? '#F59E0B'
        : '#6366F1';

  const content = (
    <div style={{ width: 280 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <Text strong style={{ fontSize: 14, color: 'var(--color-text-primary)' }} data-testid="saldo-atual">
          {textoDeSaldo(saldo.atual)}{' '}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {t('credits.of', 'de')} {textoDeSaldo(saldo.total)}
          </Text>
        </Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {saldo.pct === null
            ? t('credits.unknownBalance', 'saldo indisponível')
            : `${saldo.pct}% ${t('credits.remaining', 'restante')}`}
        </Text>
      </div>
      <Progress
        percent={saldo.pct ?? 0}
        showInfo={false}
        strokeColor={alertColor}
        trailColor="#F1F5F9"
        size="small"
        style={{ marginBottom: 0 }}
        aria-label={
          saldo.pct === null
            ? 'Saldo de créditos: não foi possível ler'
            : `Saldo de créditos: ${saldo.pct}%`
        }
      />
      {!saldo.conhecido && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
          {t(
            'credits.balanceUnavailable',
            'Não foi possível ler seu saldo agora. O número volta assim que a leitura responder.',
          )}
        </Text>
      )}

      <Divider style={{ margin: '14px 0 10px' }} />

      <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {t('credits.rates', 'Tabela de custos')}
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {OPERATION_RATES.map((op) => {
          const custo = custoDe(op.slug);
          return (
          <div
            key={op.labelKey}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#334155', fontSize: 13 }}>
              <span style={{ color: 'var(--color-text-tertiary)', display: 'flex' }}>{op.icon}</span>
              {t(op.labelKey, op.fallback)}
            </div>
            <span
              data-testid={`custo-${op.slug}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 24,
                height: 22,
                borderRadius: 6,
                background: '#F1F5F9',
                color: '#475569',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {/* `null` = catálogo em voo, fora do ar, ou operação sem
                  entrada. Um `0` aqui afirmaria gratuidade que ninguém
                  declarou; um número escrito à mão afirmaria um preço que
                  o catálogo pode ter mudado ontem. */}
              {textoDeCusto(custo)}
            </span>
          </div>
          );
        })}
      </div>

      <Divider style={{ margin: '14px 0 10px' }} />

      <a
        onClick={() => navigate('/admin/billing')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          fontSize: 12,
          color: '#6366F1',
          cursor: 'pointer',
          fontWeight: 500,
        }}
      >
        {t('credits.managePlan', 'Planos & Cobrancas')}
        <ArrowUpRight size={12} />
      </a>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ borderRadius: 12, padding: 16 }}
    >
      <button
        className="flex items-center gap-2 rounded-full hover:bg-slate-100 transition-colors cursor-pointer border-0"
        style={{
          background: '#F8FAFC',
          borderRadius: 999,
          padding: '5px 12px',
          border: '1px solid #E2E8F0',
          cursor: 'pointer',
        }}
      >
        <Coins size={14} style={{ color: alertColor }} />
        {/* O número do CABEÇALHO — o mais visto da plataforma. Com `?? 0` ele
            passava a vida piscando "0" a cada recarga de página, antes de a
            leitura responder. */}
        <span
          style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}
          data-testid="saldo-no-cabecalho"
        >
          {textoDeSaldo(saldo.atual)}
        </span>
        <ChevronDown size={11} style={{ color: 'var(--color-text-tertiary)' }} />
      </button>
    </Popover>
  );
};

export default CreditBalancePopover;
